import { GameScene } from '../GameScene';
import { createSlider } from '../../ui/formControls';
import { domDragCursorCss } from '../../ui/cursors';

/**
 * Panel de debug de iluminación / hora del día (dev tool, DOM overlay): slider de hora manual,
 * labels de tiempo, info de oscuridad y el toggle/destroy del panel. Extraído de GameScene
 * (god-object). El estado de lighting/time-of-day (lightingSystem/timeOfDayManager/useManualTime…)
 * vive en GameScene (host) y se accede vía `this.scene.*`.
 */
export class LightingDebugController {
  constructor(private scene: GameScene) {}

  public createLightingDebugPanel(): void {
    // Create container div
    this.scene.lightingDebugPanel = document.createElement('div');
    this.scene.lightingDebugPanel.id = 'lighting-debug-panel';
    this.scene.lightingDebugPanel.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.75);
      border: 1px solid #444;
      border-radius: 8px;
      padding: 12px 16px;
      color: #fff;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 12px;
      z-index: 1000;
      min-width: 180px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    `;

    // Title
    const title = document.createElement('div');
    title.textContent = '🌅 Debug: Hora del Día';
    title.style.cssText = `
      font-weight: bold;
      margin-bottom: 10px;
      font-size: 13px;
      color: #ffd54f;
    `;
    this.scene.lightingDebugPanel.appendChild(title);

    // Hacer el panel arrastrable desde la barra de título (DOM, position: fixed).
    title.style.cursor = domDragCursorCss();
    title.style.userSelect = 'none';
    const panel = this.scene.lightingDebugPanel;
    title.addEventListener('mousedown', (e: MouseEvent) => {
      const rect = panel.getBoundingClientRect();
      const dx = e.clientX - rect.left;
      const dy = e.clientY - rect.top;
      panel.style.right = 'auto';
      panel.style.left = `${rect.left}px`;
      panel.style.top = `${rect.top}px`;
      const onMove = (ev: MouseEvent): void => {
        panel.style.left = `${ev.clientX - dx}px`;
        panel.style.top = `${ev.clientY - dy}px`;
      };
      const onUp = (): void => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      e.preventDefault();
    });

    // Time label
    this.scene.timeLabel = document.createElement('span');
    this.scene.timeLabel.style.cssText = `
      display: block;
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: bold;
      text-align: center;
      color: #fff;
    `;
    this.updateTimeLabel(12);
    this.scene.lightingDebugPanel.appendChild(this.scene.timeLabel);

    // Slider container
    const sliderContainer = document.createElement('div');
    sliderContainer.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Min label
    const minLabel = document.createElement('span');
    minLabel.textContent = '🌙';
    minLabel.style.fontSize = '14px';
    sliderContainer.appendChild(minLabel);

    // Slider del kit (handle dorado), conservando la pista con gradiente día/noche
    // (es informativa) y sin fill de progreso. Ver client/src/ui/formControls.ts.
    this.scene.timeSlider = createSlider({
      min: 0,
      max: 24,
      step: 0.5,
      value: 12,
      grooveBackground:
        'linear-gradient(to right, #1a237e, #42a5f5, #fff59d, #ff7043, #1a237e)',
      hideFill: true,
      onChange: (value) => {
        this.scene.manualHour = value;
        this.scene.useManualTime = true;
        this.updateTimeLabel(value);
      },
    });
    this.scene.timeSlider.bar.style.flex = '1';
    sliderContainer.appendChild(this.scene.timeSlider.bar);

    // Max label
    const maxLabel = document.createElement('span');
    maxLabel.textContent = '☀️';
    maxLabel.style.fontSize = '14px';
    sliderContainer.appendChild(maxLabel);

    this.scene.lightingDebugPanel.appendChild(sliderContainer);

    // Shadow offset info
    const infoContainer = document.createElement('div');
    infoContainer.id = 'shadow-offset-info';
    infoContainer.style.cssText = `
      margin-top: 10px;
      padding-top: 8px;
      border-top: 1px solid #444;
      font-size: 11px;
      color: #aaa;
    `;
    this.scene.lightingDebugPanel.appendChild(infoContainer);

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = '↻ Usar hora real';
    resetBtn.style.cssText = `
      margin-top: 10px;
      width: 100%;
      padding: 6px;
      background: #424242;
      border: 1px solid #666;
      border-radius: 4px;
      color: #fff;
      cursor: pointer;
      font-size: 11px;
      transition: background 0.2s;
    `;
    resetBtn.addEventListener('mouseenter', () => {
      resetBtn.style.background = '#616161';
    });
    resetBtn.addEventListener('mouseleave', () => {
      resetBtn.style.background = '#424242';
    });
    resetBtn.addEventListener('click', () => {
      this.scene.useManualTime = false;
      const now = new Date();
      const realHour = now.getHours() + now.getMinutes() / 60;
      if (this.scene.timeSlider) {
        this.scene.timeSlider.setValue(realHour);
      }
      this.updateTimeLabel(realHour);
    });
    this.scene.lightingDebugPanel.appendChild(resetBtn);

    // Oculto por default: se muestra con el botón ☀️ del header.
    this.scene.lightingDebugPanel.style.display = 'none';

    // Append to body
    document.body.appendChild(this.scene.lightingDebugPanel);
  }

  /** Muestra/oculta el panel debug de hora del día (botón ☀️). */
  public toggleLightingDebugPanel(): void {
    if (!this.scene.lightingDebugPanel) return;
    const isHidden = this.scene.lightingDebugPanel.style.display === 'none';
    this.scene.lightingDebugPanel.style.display = isHidden ? 'block' : 'none';
  }

  /**
   * Updates the time label with formatted hour and visual icon
   */
  public updateTimeLabel(hour: number): void {
    if (!this.scene.timeLabel) return;
    
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    // Choose icon based on time of day
    let icon = '☀️';
    if (hour >= 6 && hour < 8) icon = '🌅';
    else if (hour >= 8 && hour < 17) icon = '☀️';
    else if (hour >= 17 && hour < 19) icon = '🌇';
    else if (hour >= 19 && hour < 21) icon = '🌆';
    else icon = '🌙';
    
    this.scene.timeLabel.textContent = `${icon} ${timeStr}`;
  }

  /**
   * Updates the lighting info in the debug panel
   */
  public updateDebugPanelInfo(darkness?: number): void {
    const infoEl = document.getElementById('shadow-offset-info');
    if (infoEl) {
      const darknessValue = darkness ?? (this.scene.timeOfDayManager?.getAmbientDarkness() ?? 0);
      const darknessPercent = (darknessValue * 100).toFixed(0);
      const lightCount = this.scene.lightingSystem?.getLightSourceCount() ?? 0;
      const hour = this.scene.timeOfDayManager?.getCurrentHour() ?? 12;
      infoEl.innerHTML = `
        <div>Hora: <strong>${hour.toFixed(1)}h</strong></div>
        <div>Oscuridad: <strong>${darknessPercent}%</strong></div>
        <div>Luces: <strong>${lightCount}</strong></div>
      `;
    }
  }

  /**
   * Removes the lighting debug panel from the DOM
   */
  public destroyLightingDebugPanel(): void {
    if (this.scene.lightingDebugPanel && this.scene.lightingDebugPanel.parentNode) {
      this.scene.lightingDebugPanel.parentNode.removeChild(this.scene.lightingDebugPanel);
      this.scene.lightingDebugPanel = null;
      this.scene.timeSlider = null;
      this.scene.timeLabel = null;
    }
  }
}
